import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form, FormItem, Input } from '@packages/utils';
import { Select } from 'components/Base/Select';
import {
  useGetPillarsListQuery,
  useGetPillarsOfStrengthQuery,
} from 'redux/apiSlice/pillars.slice';
import {
  useAddCustomTacticMutation,
  useCreateTaskFromMeetingMutation,
} from 'redux/apiSlice/pillarsActionPlan.slice';
import { TAddCustomTacticRequest } from 'data/types/pillarsActionPlan.types';
import { TPillarsUI } from 'data/types/pillars.types';
import useForm from 'hooks/useForm';
import {
  useShowToastNotification,
  ENotificationType,
} from 'components/Base/Notification';
import {
  StyledModal,
  ModalFooter,
  CancelButton,
  PrimaryButton,
  FormContainer,
} from 'components/Shared/StyledModal';
import { GoalLevelFields } from 'pages/private/ELCs/ActionPlan/TacticTaskTable/TacticModals/GoalLevelFields';
import {
  TAddTacticFromActionItemModalProps,
  TAddTacticFormValues,
} from './AddTacticFromActionItemModal.types';

const AddTacticFromActionItemModal: React.FC<
  TAddTacticFromActionItemModalProps
> = ({ open, onClose, selectedActionItems, elcId }) => {
  const { form } = useForm<TAddTacticFormValues>();
  const { showToastNotification } = useShowToastNotification();
  const [addCustomTactic, { isLoading: isAddingTactic }] =
    useAddCustomTacticMutation();
  const [createTaskFromMeeting, { isLoading: isCreatingTask }] =
    useCreateTaskFromMeetingMutation();
  const [tacticNameError, setTacticNameError] = useState<string | null>(null);

  const [selectedPillarId, setSelectedPillarId] = useState<
    string | undefined
  >();
  const [selectedSubPillarId, setSelectedSubPillarId] = useState<
    string | undefined
  >();

  const { data: pillarsData, isLoading: pillarsLoading } =
    useGetPillarsListQuery();

  const {
    data: pillarsOfStrength,
    isError: pillarsOfStrengthError,
    error: pillarsOfStrengthErrorPayload,
  } = useGetPillarsOfStrengthQuery({ elcId: elcId ?? '' }, { skip: !open });

  const pillarsErrorToastShown = useRef(false);

  useEffect(() => {
    if (!open) {
      pillarsErrorToastShown.current = false;
      return;
    }
    if (pillarsOfStrengthError && !pillarsErrorToastShown.current) {
      pillarsErrorToastShown.current = true;
      const message =
        (pillarsOfStrengthErrorPayload as any)?.data?.message ||
        'Unable to load Goal and Level options. Please try again or contact support.';
      showToastNotification({
        type: ENotificationType.ERROR,
        message,
      });
    }
  }, [
    open,
    pillarsOfStrengthError,
    pillarsOfStrengthErrorPayload,
    showToastNotification,
  ]);

  const isLoading = isAddingTactic || isCreatingTask;

  // Map pillar names to the correct IDs expected by the backend
  // NOTE THIS IS TEMPORARY, Until we implement the new API call for pillars without using the JSON FILE
  const pillarNameToIdMap: Record<string, string> = {
    Communities: '1',
    Services: '2',
    Neighborhoods: '3',
    Policies: '4',
  };

  const pillarsOptions = useMemo(
    () =>
      pillarsData?.pillars?.map((pillar: TPillarsUI) => ({
        label: pillar.pillarName,
        value: pillarNameToIdMap[pillar.pillarName] || pillar.pillarId,
      })) ?? [],
    [pillarsData],
  );

  const handlePillarChange = () => {
    form.setFieldsValue({ subPillarId: undefined, levelId: undefined });
    setSelectedSubPillarId(undefined);
  };

  const handleSubPillarChange = () => {
    form.setFieldsValue({ levelId: undefined });
  };

  const handleSubmit = async () => {
    if (!elcId) return;

    try {
      const values = await form.validateFields();

      const requestBody: TAddCustomTacticRequest = {
        elcId,
        title: values.tacticName,
        pillarsId: values.pillarId,
      };
      if (values.subPillarId) requestBody.subPillarId = values.subPillarId;
      if (values.levelId) requestBody.levelId = values.levelId;

      const tacticResponse = await addCustomTactic(requestBody).unwrap();

      // Step 2: Create tasks from selected action items using the new convert-from-meeting endpoint
      if (selectedActionItems.length > 0) {
        const meetingActionItemIds = selectedActionItems.map(
          actionItem => actionItem.id,
        );

        await createTaskFromMeeting({
          actionPlanId: Number(tacticResponse.id),
          meetingActionItemIds,
        }).unwrap();
      }

      // Show success notification
      showToastNotification({
        type: ENotificationType.SUCCESS,
        message: `The Tactic was added to Action Plan`,
      });

      // Close modal and reset form
      form.resetFields();
      setTacticNameError(null);
      setSelectedPillarId(undefined);
      setSelectedSubPillarId(undefined);
      onClose();
    } catch (error: any) {
      // Check if it's a 400 error with "already exist" message
      if (
        error?.status === 400 &&
        error?.data?.message?.includes('already exist')
      ) {
        const errorMessage =
          'This tactic title already exists. Please choose a different name.';

        setTacticNameError(errorMessage);
        form.setFields([
          {
            name: 'tacticName',
            errors: [errorMessage],
          },
        ]);

        showToastNotification({
          type: ENotificationType.ERROR,
          message: errorMessage,
        });

        return;
      }

      // For other errors, show generic message
      showToastNotification({
        type: ENotificationType.ERROR,
        message: 'Failed to add tactic. Please try again.',
      });
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setTacticNameError(null);
    setSelectedPillarId(undefined);
    setSelectedSubPillarId(undefined);
    onClose();
  };

  const handleTacticNameChange = () => {
    if (tacticNameError) {
      setTacticNameError(null);
      form.setFields([
        {
          name: 'tacticName',
          errors: [],
        },
      ]);
    }
  };

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setTacticNameError(null);
      setSelectedPillarId(undefined);
      setSelectedSubPillarId(undefined);
    }
  }, [open, form]);

  return (
    <StyledModal
      title="Add Tactic"
      open={open}
      onCancel={handleCancel}
      footer={null}
      centered
      closeIcon={<span>×</span>}
      width={600}
      data-cy="add_tactic_modal"
    >
      <FormContainer>
        <Form
          form={form}
          layout="vertical"
          requiredMark="optional"
          onValuesChange={changed => {
            if ('pillarId' in changed) {
              setSelectedPillarId(changed.pillarId);
            }
            if ('subPillarId' in changed) {
              setSelectedSubPillarId(changed.subPillarId);
            }
          }}
        >
          <FormItem
            label="Tactic Name"
            name="tacticName"
            rules={[
              { required: true, message: 'Please enter tactic name' },
              { whitespace: true, message: 'Tactic name cannot be empty' },
              { max: 200, message: 'Tactic name cannot exceed 200 characters' },
            ]}
          >
            <Input
              placeholder="Enter Name"
              autoFocus
              onChange={handleTacticNameChange}
              maxLength={200}
              data-cy="add_tactic_name_input"
            />
          </FormItem>

          <FormItem
            label="Pillar"
            name="pillarId"
            rules={[{ required: true, message: 'Please select a pillar' }]}
          >
            <Select
              placeholder="Select Pillar"
              options={pillarsOptions}
              loading={pillarsLoading}
              showSearch
              optionFilterProp="label"
              onChange={handlePillarChange}
              data-cy="add_tactic_pillar_select"
            />
          </FormItem>

          <GoalLevelFields
            pillarsOfStrength={pillarsOfStrength}
            selectedPillarId={selectedPillarId}
            selectedSubPillarId={selectedSubPillarId}
            onGoalChange={handleSubPillarChange}
            goalDataCy="add_tactic_goal_select"
            levelDataCy="add_tactic_level_select"
          />
        </Form>

        <ModalFooter>
          <CancelButton
            onClick={handleCancel}
            color="primary"
            disabled={isLoading}
            data-cy="add_tactic_cancel_button"
          >
            Cancel
          </CancelButton>
          <PrimaryButton
            type="primary"
            onClick={handleSubmit}
            color="primary"
            loading={isLoading}
            disabled={
              selectedActionItems.length === 0 || pillarsOfStrengthError
            }
            data-cy="add_tactic_submit_button"
          >
            Add
          </PrimaryButton>
        </ModalFooter>
      </FormContainer>
    </StyledModal>
  );
};

export default AddTacticFromActionItemModal;
